import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '@/api/client';
import { LoginModal } from '@/components/modals/LoginModal';
import { AppShell } from '@/components/layout/AppShell';
import { MobileShell } from '@/components/mobile/MobileShell';
import { ToastContainer } from '@/components/shared/ToastContainer';
import { PromptDialog } from '@/components/shared/PromptDialog';
import { LoadingScreen } from '@/components/shared/Skeleton';
import { restoreState } from '@/hooks/useStateSync';
import { useMobileLayout } from '@/hooks/useMobileLayout';
import { useSuppressNativeContextMenu } from '@/hooks/useSuppressNativeContextMenu';
import { useUIFontVars } from '@/hooks/useUIFontVars';
import { useSettingsStore } from '@/stores/settings-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useUsageStore, useGlobalSchemaStore } from '@/lib/commands';

function GlobalPromptDialog() {
  const promptState = useDialogStore((s) => s.prompt);
  const closePrompt = useDialogStore((s) => s.closePrompt);
  if (!promptState.open) return null;
  return (
    <PromptDialog
      title={promptState.title}
      message={promptState.message}
      defaultValue={promptState.defaultValue}
      placeholder={promptState.placeholder}
      onSubmit={(value) => closePrompt(value)}
      onCancel={() => closePrompt(null)}
    />
  );
}

const auth0Enabled = !!(import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID);

export default function App() {
  // Runs once at the app root so every surface (desktop + mobile, local +
  // Auth0) gets the same native-context-menu suppression. Critical on iPad
  // where Safari injects Writing Tools into the right-click menu.
  useSuppressNativeContextMenu();
  // Mirror ui.fontFamily / ui.fontSize settings to CSS custom properties so
  // form inputs pick up the user's chosen UI font.
  useUIFontVars();

  if (auth0Enabled) {
    return <Auth0App />;
  }
  return <LocalApp />;
}

function Auth0App() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout } = useAuth0();
  const [ready, setReady] = useState(false);
  const [accessError, setAccessError] = useState('');
  const isMobile = useMobileLayout();

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      loginWithRedirect().catch(() => {
        // Redirect failed — will be handled by Auth0 SDK
      });
      return;
    }

    // Get access token — try cache first, then silent auth, then redirect
    (async () => {
      let token: string | null = null;

      // Try 1: use cached token (instant, no network)
      try {
        token = await getAccessTokenSilently();
      } catch {
        // Cache miss or expired — try silent auth (iframe)
      }

      // Try 2: if no cached token, try silent auth with fresh fetch
      if (!token) {
        try {
          token = await getAccessTokenSilently({ cacheMode: 'off' });
        } catch (err: unknown) {
          // Silent auth failed (common on mobile Safari due to ITP blocking iframes)
          // Fall back to full-page redirect which always works
          const msg = err instanceof Error ? err.message : '';
          if (msg.includes('login_required') || msg.includes('Login required') ||
              msg.includes('Consent required') || msg.includes('timeout') ||
              msg.includes('Missing Refresh Token')) {
            loginWithRedirect().catch(() => {});
            return;
          }
          setAccessError(msg || 'Failed to get access token');
          return;
        }
      }

      if (!token) {
        loginWithRedirect().catch(() => {});
        return;
      }

      localStorage.setItem('dbgo_token', token);

      // Bootstrap order: /me/touch FIRST so the server's app_users row
      // exists (server hits Auth0 /userinfo to resolve the verified
      // email and writes role + active defaults). Without it, every
      // subsequent request would 401 with "User not yet provisioned".
      // After the touch, /api/state and friends work normally.
      try {
        await apiClient.post('/api/auth/me/touch', {});
      } catch (err) {
        // Touch failures are recoverable on next reload — but the user
        // can't actually do anything until app_users has a row, so
        // surface it.
        const msg = err instanceof Error ? err.message : 'Provisioning failed';
        setAccessError(msg);
        return;
      }

      // Verify we have dbgo:access permission by calling the API
      try {
        await apiClient.get('/api/state');
        await restoreState();
        await useSettingsStore.getState().load();
        await useUsageStore.getState().load();
        // Global schema cache loads in the background — the command bar
        // works with whatever's ready. Don't await.
        useGlobalSchemaStore.getState().load();
        setReady(true);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Access denied';
        if (msg.includes('do not have access') || msg.includes('403')) {
          setAccessError('You do not have access to DBGo. Please contact harry.sitorus@ayana.com to request the dbgo:access permission.');
        } else {
          setAccessError(msg);
        }
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect]);

  // Token refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      getAccessTokenSilently()
        .then((token) => {
          localStorage.setItem('dbgo_token', token);
        })
        .catch(() => {
          // Token refresh failed — will redirect to login on next API call
        });
    }, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessTokenSilently]);

  // Register the Auth0 SDK's logout function with apiClient. The sidebar
  // / mobile MoreScreen / API Keys panel all call `apiClient.logout()`
  // and need this hook to actually clear the Auth0 SDK's internal
  // session — without it the SDK silently re-auths on the next page
  // load and the user appears to stay signed in.
  useEffect(() => {
    apiClient.setAuth0Logout(logout);
  }, [logout]);

  // Remove the HTML splash screen once React has taken over
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (splash) {
      splash.classList.add('hide');
      setTimeout(() => splash.remove(), 300);
    }
  }, [ready, accessError]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (accessError) {
    return (
      <div className="h-screen flex items-center justify-center bg-dbgo-root">
        <div className="w-[400px] bg-dbgo-modal border rounded-lg p-6 shadow-2xl text-center">
          <div className="text-xl font-bold text-text-primary mb-2">DBGo</div>
          <div className="text-accent-red text-sm mb-4">Access Denied</div>
          <div className="text-text-secondary text-xs mb-4">{accessError}</div>
          <button
            onClick={() => {
              // Clear cached token and force fresh Auth0 login
              localStorage.removeItem('dbgo_token');
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
            className="px-4 py-2 text-xs bg-accent-blue text-white rounded-sm hover:bg-accent-blue/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return <LoadingScreen />;
  }

  return (
    <>
      {isMobile ? <MobileShell /> : <AppShell />}
      <ToastContainer />
      <GlobalPromptDialog />
    </>
  );
}

function LocalApp() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const isMobile = useMobileLayout();

  useEffect(() => {
    const token = localStorage.getItem('dbgo_token');
    if (!token) {
      setAuthenticated(false);
      return;
    }
    // Verify token by calling the state endpoint
    apiClient.get('/api/state')
      .then(async () => {
        // Refresh known_users directory entry so this user can be picked
        // for sharing. Local users get display_name from the JWT; email is
        // typically null in local mode — that's fine.
        const me = apiClient.getCurrentUser();
        if (me) {
          apiClient.post('/api/auth/me/touch', {
            displayName: me.displayName ?? me.username,
          }).catch(() => { /* best-effort */ });
        }
        await restoreState();
        await useSettingsStore.getState().load();
        await useUsageStore.getState().load();
        useGlobalSchemaStore.getState().load();
        setAuthenticated(true);
      })
      .catch(() => {
        localStorage.removeItem('dbgo_token');
        localStorage.removeItem('dbgo_user');
        setAuthenticated(false);
      });
  }, []);

  const handleLoginSuccess = useCallback(async () => {
    const me = apiClient.getCurrentUser();
    if (me) {
      apiClient.post('/api/auth/me/touch', {
        displayName: me.displayName ?? me.username,
      }).catch(() => { /* best-effort */ });
    }
    await restoreState();
    await useSettingsStore.getState().load();
    await useUsageStore.getState().load();
    useGlobalSchemaStore.getState().load();
    setAuthenticated(true);
  }, []);

  // Remove the HTML splash screen once React has taken over
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (splash) {
      splash.classList.add('hide');
      setTimeout(() => splash.remove(), 300);
    }
  }, [authenticated]);

  if (authenticated === null) {
    return <LoadingScreen />;
  }

  if (!authenticated) {
    return <LoginModal onSuccess={handleLoginSuccess} />;
  }

  return (
    <>
      {isMobile ? <MobileShell /> : <AppShell />}
      <ToastContainer />
      <GlobalPromptDialog />
    </>
  );
}
